import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Target, Check, Flag, Lock, ChevronDown, ChevronRight, Lightbulb, ScrollText, Award } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const GROUP_ORDER = ["Account milestones", "Combat & unlocks", "Quests & diaries", "Skill capes (99)"];

function StatusPill({ status }) {
  const map = {
    done: { text: "Done", cls: "text-[#7CFC7C] border-[#2f6b1c] bg-[#16240f]", icon: <Check size={12} /> },
    available: { text: "Available", cls: "text-[#FFCF3F] border-[#7a5f1f] bg-[#241b10]", icon: <ChevronRight size={12} /> },
    locked: { text: "Locked", cls: "text-[#c98a8a] border-[#5a2020] bg-[#241010]", icon: <Lock size={12} /> },
  };
  const s = map[status] || map.locked;
  return (
    <span data-testid={`quest-status-${status}`} className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] osrs-num ${s.cls}`}>
      {s.icon}{s.text}
    </span>
  );
}

function EntryRow({ label, reward, status, need, right, why }) {
  return (
    <div data-testid="roadmap-entry" className={`px-3 py-2 border-2 border-black ${status === "done" ? "bg-[#16240f]" : status === "locked" ? "bg-[#241010]" : "bg-[#241b10]"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`osrs-shadow text-sm font-bold flex items-center gap-2 flex-wrap ${status === "done" ? "text-[#7CFC7C]" : status === "locked" ? "text-[#c98a8a]" : "text-[#E6A519]"}`}>
          {label}
          {why && <span data-testid="entry-why" className="osrs-num text-[9px] uppercase tracking-wide text-[#7CFC7C] border border-[#2f6b1c] bg-[#16240f] px-1.5 py-0.5">{why}</span>}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {right}
          <StatusPill status={status} />
        </div>
      </div>
      {reward && <p className="osrs-shadow text-[#b9a06a] text-xs mt-0.5">{reward}</p>}
      {status === "locked" && need && (
        <p className="osrs-shadow text-[#c98a8a] text-xs mt-0.5">Needs: {need}</p>
      )}
    </div>
  );
}

function Section({ section }) {
  if (section.key === "skills") {
    return (
      <div className="mb-4" data-testid="section-skills">
        <SectionHeader icon={<Target size={14} />} title={section.title} subtitle={section.subtitle} />
        <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
          {section.steps.map((s, i) => (
            <div key={i} data-testid="roadmap-step" className={`px-3 py-2 border-2 border-black ${s.done ? "bg-[#16240f]" : "bg-[#241b10]"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="osrs-shadow text-sm flex items-center gap-2">
                  {s.done ? <Check size={14} className="text-[#7CFC7C]" /> : <span className="w-3.5 h-3.5 rounded-full border border-[#6b6455] inline-block" />}
                  <span className={s.done ? "text-[#7CFC7C] line-through" : "text-[#E6A519]"}>{s.label}</span>
                </span>
                <span className="osrs-num text-xs text-[#b9a06a] shrink-0">{s.current} / {s.target}</span>
              </div>
              {!s.done && s.hint && (
                <p className="osrs-shadow text-[#8fb98f] text-xs mt-1 flex items-start gap-1">
                  <Lightbulb size={12} className="mt-0.5 shrink-0 text-[#7CFC7C]" /> {s.hint}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (section.key === "quests") {
    return (
      <div className="mb-4" data-testid="section-quests">
        <SectionHeader icon={<ScrollText size={14} />} title={section.title} sub={`${section.done_count}/${section.total_count} done`} subtitle={section.subtitle} />
        <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
          {section.quests.map((q, i) => (
            <EntryRow key={i} label={q.label} reward={q.reward} status={q.status} need={q.need} why={q.why}
              right={<span className="osrs-num text-[10px] text-[#b9a06a] shrink-0">{q.tier}</span>} />
          ))}
          {!section.quests.some((q) => q.synced) && (
            <p className="osrs-shadow text-[#b9a06a] text-xs">Sync WikiSync to mark completed quests automatically.</p>
          )}
        </div>
      </div>
    );
  }

  if (section.key === "diaries") {
    return (
      <div className="mb-4" data-testid="section-diaries">
        <SectionHeader icon={<Award size={14} />} title={section.title} sub={`${section.done_count}/${section.total_count} done`} subtitle={section.subtitle} />
        <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
          {section.diaries.map((d, i) => (
            <EntryRow key={i} label={d.label} reward={d.reward} status={d.status} need={d.need} why={d.why}
              right={<span className="osrs-num text-[10px] text-[#b9a06a] shrink-0">{d.tier}</span>} />
          ))}
        </div>
      </div>
    );
  }

  if (section.key === "tips") {
    if (!section.items?.length) return null;
    return (
      <div className="mb-1" data-testid="section-tips">
        <SectionHeader icon={<Lightbulb size={14} />} title={section.title} subtitle={section.subtitle} />
        <ul className="space-y-1.5">
          {section.items.map((t, i) => (
            <li key={i} data-testid="roadmap-tip" className="osrs-shadow text-[#d8c89a] text-xs flex items-start gap-2 px-3 py-2 border-2 border-black bg-[#20180d]">
              <span className="text-[#7CFC7C] mt-0.5">▸</span><span>{t}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
}

function SectionHeader({ icon, title, sub, subtitle }) {
  return (
    <div className="mb-1.5">
      <div className="flex items-center justify-between">
        <span className="osrs-num text-[#FFCF3F] text-xs flex items-center gap-1.5 uppercase tracking-wide">{icon}{title}</span>
        {sub && <span className="osrs-shadow text-[#E6A519] text-[10px]">{sub}</span>}
      </div>
      {subtitle && <p className="osrs-shadow text-[#9a8a5f] text-xs mt-1 leading-snug" data-testid="section-why">{subtitle}</p>}
    </div>
  );
}

export default function GoalRoadmap({ profile }) {
  const [goals, setGoals] = useState([]);
  const [current, setCurrent] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openCapes, setOpenCapes] = useState(false);

  const load = async () => {
    try {
      const [{ data: gl }, { data: rm }] = await Promise.all([
        api.get("/goals"),
        api.get(`/profiles/${profile.id}/roadmap`),
      ]);
      setGoals(gl);
      setCurrent(rm.goal);
      setRoadmap(rm.roadmap);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [profile.id, profile.updated_at]);

  const pick = async (goalId) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/profiles/${profile.id}/goal?goal=${goalId}`);
      setCurrent(data.goal);
      setRoadmap(data.roadmap);
      if (goalId) toast.success("Goal set! Here's your roadmap.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const grouped = GROUP_ORDER.map((g) => ({ group: g, items: goals.filter((x) => x.group === g) })).filter((x) => x.items.length);

  const GoalButton = ({ g }) => (
    <button
      key={g.id}
      data-testid={`goal-${g.id}`}
      onClick={() => pick(current === g.id ? "" : g.id)}
      disabled={busy}
      title={g.desc}
      className={`osrs-btn px-3 py-1.5 text-xs ${current === g.id ? "osrs-btn-green" : "opacity-80"}`}
    >
      {g.name}
    </button>
  );

  return (
    <div className="osrs-panel p-5 mt-6" data-testid="goal-roadmap">
      <div className="flex items-center gap-2 mb-3">
        <Target size={18} className="text-[#FFCF3F]" />
        <h3 className="osrs-title text-lg">Goal Roadmap</h3>
      </div>

      <div className="space-y-3 mb-4">
        {grouped.map(({ group, items }) => {
          const isCapes = group === "Skill capes (99)";
          const selectedCape = isCapes && items.some((g) => g.id === current);
          return (
            <div key={group}>
              {isCapes ? (
                <button
                  data-testid="toggle-skill-capes"
                  onClick={() => setOpenCapes((v) => !v)}
                  className="osrs-shadow text-[#b9a06a] text-[11px] uppercase tracking-wide mb-1.5 flex items-center gap-1 hover:text-[#FFCF3F]"
                >
                  {openCapes || selectedCape ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {group}
                </button>
              ) : (
                <p className="osrs-shadow text-[#b9a06a] text-[11px] uppercase tracking-wide mb-1.5">{group}</p>
              )}
              {(!isCapes || openCapes || selectedCape) && (
                <div className="flex flex-wrap gap-2">
                  {items.map((g) => <GoalButton key={g.id} g={g} />)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!current && (
        <p className="osrs-shadow text-[#b9a06a] text-sm">Pick a dream goal above and TaskScape maps out every skill, quest and diary you need — with ideas on how to get there, tracked live from your stats.</p>
      )}

      {current && roadmap && (
        <div data-testid="roadmap-body">
          <div className="flex items-center justify-between mb-1">
            <span className="osrs-num text-[#FFCF3F] text-sm">{roadmap.goal.name}</span>
            <span className="osrs-shadow text-[#E6A519] text-sm">{roadmap.done_count}/{roadmap.total_count} · {roadmap.percent}%</span>
          </div>
          <div className="w-full h-3 bg-[#241b10] border-2 border-black mb-4">
            <div className="h-full bg-[#4ade80]" style={{ width: `${roadmap.percent}%`, transition: "width 0.4s ease" }} data-testid="roadmap-progress" />
          </div>
          {roadmap.complete && (
            <p className="osrs-num text-[#7CFC7C] text-center py-2 mb-3 flex items-center justify-center gap-2 border-2 border-[#2f6b1c] bg-[#16240f]" data-testid="roadmap-complete">
              <Flag size={16} /> Goal achieved — legendary!
            </p>
          )}
          {(roadmap.sections || []).map((s, i) => <Section key={i} section={s} />)}
        </div>
      )}
    </div>
  );
}
